import React, { useEffect, useState } from "react";
import styles from "./school-academic-year.module.css";
import { Typography } from "antd";
import CustomButton from "../../../../../../../common/custom-button/custom-button";
import { PlusOutlined } from "@ant-design/icons";
import CustomTable from "../../../../../../../common/custom-table/custom-table";
import { fetchAcademicYear } from "../../../../../../../services/class_routine";
import dayjs from "dayjs";
import SVGIcon from "../../../../../../../constants/SVGIcon";
import AddEditAcademicYear from "./components/add-edit-academic-year/add-edit-academic-year";
import CustomPopover from "../../../../../../../common/custom-popover/custom-popover";
import { deleteAcademicYears } from "../../../../../../../services/school_info";
import { toast } from "react-toastify";
import { DATE_FORMAT } from "../../../../../../../constants/common";
import { refetchSchool } from "../../../../../../../utils/common";

const SchoolAcademicYear = () => {
  const [academicYears, setAcademicYear] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editDetails, setEditDetails] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setPageNo(1);
    getAcademicYears();
  }, []);

  const getAcademicYears = async (tempPage = pageNo, tempPageSize = pageSize) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
      };
      const response = await fetchAcademicYear(payload);
      if (response?.data) {
        const data = response?.data?.data?.academicYears;
        setAcademicYear(data);
        setTotalCount(response?.data?.data?.totalRecords);
      }
      setLoading(false);
    } catch (err) {
      if (err?.data?.message) {
        toast.error(err?.data?.message);
      }
      console.log(err);
      setLoading(false);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getAcademicYears(page, pageSize);
  };

  const handleEditAcademicYear = (data) => {
    setEditDetails(data);
    setOpenDrawer(true);
  };

  const handleOnClose = () => {
    getAcademicYears();
    setOpenDrawer(false);
  };

  const handleDeleteAcademicYear = async (id) => {
    try {
      await deleteAcademicYears(id);
      getAcademicYears();
      refetchSchool();
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || "Error deleting academic year");
    }
  };

  const handleAddAcademicYears = () => {
    setEditDetails(null);
    setOpenDrawer(true);
  };
  
  const columns = [
    {
      title: "Year",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a?.name - b?.name,
      render: (value) => value || "-",
    },
    {
      title: "Start date",
      dataIndex: "start_date",
      key: "start_date",
      sorter: (a, b) => dayjs(a?.start_date, DATE_FORMAT.DD_MM_YYYY).valueOf() - dayjs(b?.start_date, DATE_FORMAT.DD_MM_YYYY).valueOf(),
      render: (value) => value || "-",
    },
    {
      title: "End date",
      dataIndex: "end_date",
      key: "end_date",
      sorter: (a, b) => dayjs(a?.end_date, DATE_FORMAT.DD_MM_YYYY).valueOf() - dayjs(b?.end_date, DATE_FORMAT.DD_MM_YYYY).valueOf(),
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "active_year",
      key: "active_year",
      sorter: (a, b) => b?.is_active - a?.is_active, // true < false (Active before Inactive)
      render: (value) => (
        <div className={`${styles.statusTag} ${value ? styles.activeTag : styles.inactiveTag}`}>
          <span className={`${styles.statusDot} ${value ? styles.activeDot : styles.inactiveDot}`}/>
          <span>{value ? "Active" : "Inactive"}</span>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => {
        const today = dayjs();
        const startDate = dayjs(record?.start_date, DATE_FORMAT.DD_MM_YYYY);
        const endDate = dayjs(record?.end_date, DATE_FORMAT.DD_MM_YYYY);

        const isFutureYear = startDate.isAfter(today);
        const isPastYear = endDate.isBefore(today);

        return (
          <div className={styles.actionItemWrapper}>
            {isPastYear ? (
              <SVGIcon
                name={"edit-icon"}
                width={"20"}
                height={"20"}
                stroke={"rgba(0, 0, 0, 0.25)"}
                style={{ cursor: "not-allowed" }}
              />
            ) : (
              <SVGIcon
                name={"edit-icon"}
                width={"20"}
                height={"20"}
                stroke={"#3BB44B"}
                onClick={() => handleEditAcademicYear(record)}
              />
            )}
            {isFutureYear ? (
              <CustomPopover
                message="Are you sure you want to delete this Academic Year?"
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={() => handleDeleteAcademicYear(record?.id)}
              >
                <SVGIcon
                  name={"delete"}
                  width={"20"}
                  height={"20"}
                  stroke={"rgba(243, 17, 17, 1)"}
                />
              </CustomPopover>
            ) : (
              <SVGIcon
                name="delete"
                width="20"
                height="20"
                stroke="rgba(0, 0, 0, 0.25)"
                style={{ cursor: "not-allowed" }}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className={styles.titleContainer}>
        <Typography className={styles.tabTitle}>
          Academic Year List
        </Typography>
        <CustomButton
          title="Add New Academic Year"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleAddAcademicYears}
        />
      </div>

      <div className={styles.tableContainer}>
        <CustomTable
          columns={columns}
          dataSource={academicYears}
          loading={loading}
          headerColor="#667085"
          headerBackgroundColor="#F9FAFB"
          total={totalCount}
          pageSize={pageSize}
          currentPage={pageNo}
          handlePageChange={handleChangePage}
        />
      </div>
      <AddEditAcademicYear 
        open={openDrawer} 
        onClose={handleOnClose} 
        data={editDetails}
        existingYears={academicYears?.map((item) => Number(item?.name))}
      />
    </div>
  );
};

export default SchoolAcademicYear;